import {useInjection} from "inversify-react";
import {useEffect, useState} from "react";
import ApiService from "../../services/apiService";
import {
    MenuCategoryApi, Direction,
    MenuCategoryDto,
    MenuEntryApi,
    MenuEntryCreateDto,
    MenuEntryDto,
    MenuEntryUpdateDto, PriceCreateDto, PriceDto, PriceUpdateDto, PriceApi
} from "../../api";
import MenuEntryList from "./menuEntryList";
import MenuEntryModalFormAddContainer from "./menuEntryModalFormAddContainer/menuEntryModalFormAddContainer";
import {Layout, notification, Typography} from "antd";
import {Content} from "antd/lib/layout/layout";
import styles from "./menuEntryListContainer.module.scss";
import {useParams} from "react-router-dom";

const {Title} = Typography;

export default function MenuEntryListContainer() {
    const apiService = useInjection(ApiService);
    const {menuCategoryId} = useParams<{ menuCategoryId: string }>();
    const [menuEntry, setMenuEntry] = useState<MenuEntryDto[] | null>();
    const [menuCategories, setMenuCategories] = useState<MenuCategoryDto[] | null>();
    const [prices, setPrices] = useState<PriceDto[] | null>([]);
    const [isAddFormVisible, setAddFormVisible] = useState(false);
    const [menuEntryModalFormContainerTitle, setMenuEntryModalFormContainerTitle] = useState<string>();
    const [menuEntryToEdit, setMenuEntryToEdit] = useState<MenuEntryUpdateDto | null>(null);
    const [idMenuEntryToEdit, setIdMenuEntryToEdit] = useState<number | null>(null);
    const [pricesToEdit, setPricesToEdit] = useState<PriceUpdateDto[]>([]);

    useEffect(() => {
        fetchMenuCategories();
        refreshMenuEntryList();
    }, [])

    const fetchMenuCategories = async () => {
        try {
            const categories = await apiService.getApi(MenuCategoryApi).apiMenuCategoryGet();
            setMenuCategories(categories.data);
        } catch (e: any) {
            console.log(e.response.data);
        }
    }

    const refreshMenuEntryList = async () => {
        try {
            const MenuEntry = await apiService.getApi(MenuEntryApi).apiMenuEntryGet();
            let filteredMenuEntry = MenuEntry.data.filter(s => s.menuCategoryId == +menuCategoryId!)
            let sortedMenuEntries = filteredMenuEntry.sort((a, b) => {
                return a.position! - b.position!
            })
            setMenuEntry(sortedMenuEntries)
        } catch (e: any) {
            console.log(e.response.data);
        }
    }

    const onAddClicked = () => {
        setPrices([]);
        setMenuEntryModalFormContainerTitle("Dodaj nowy wpis w menu");
        setMenuEntryToEdit(null);
        setIdMenuEntryToEdit(null);
        setAddFormVisible(true);
    }

    const onEditClicked = async (id: number) => {
        setPrices([]);
        setMenuEntryModalFormContainerTitle(`Edytuj wpis menu z id: ${id}`);
        const response = await apiService.getApi(MenuEntryApi).apiMenuEntryIdGet(id);

        setMenuEntryToEdit(response.data as MenuEntryUpdateDto);
        setIdMenuEntryToEdit(id);
        setAddFormVisible(true);
    }

    const onDeleteClicked = async (id: number) => {
        const response = await apiService.getApi(MenuEntryApi).apiMenuEntryIdDelete(id)

        if (response.request.status !== 200) {
            console.log("Error occured while deleting data"); //add some warning?
        } else {
            setPrices([])
            await refreshMenuEntryList();
        }
    }

    const openNotification = (message: string) => {
        notification.info({
            message: message,
            description:
                'Aby wprowadzone zmiany były widoczne na stronie konieczna jest publikacja',
            placement:"top"
        });
    };

    const onSubmit = async (values: MenuEntryCreateDto | MenuEntryUpdateDto) => {
        try {
            let menuEntry = values;
            menuEntry.menuCategoryId = values.menuCategoryId === undefined ? +menuCategoryId! : values.menuCategoryId;
            if (menuEntryToEdit === null) {
                menuEntry.prices = prices;
                await apiService.getApi(MenuEntryApi).apiMenuEntryPost(menuEntry);
                openNotification("Pomyślnie dodano nowy wpis");
            } else if (idMenuEntryToEdit !== null) {
                menuEntry.position = menuEntryToEdit.position;
                menuEntry.prices = prices
                await apiService.getApi(MenuEntryApi).apiMenuEntryIdPut(idMenuEntryToEdit, menuEntry)
                for (let priceToEdit of pricesToEdit) {
                    if (priceToEdit?.id) {
                        let priceUpdateDto: PriceUpdateDto = {
                            value: priceToEdit.value,
                            description: priceToEdit.description,
                            menuEntryId: menuEntryToEdit.id
                        }
                        await apiService.getApi(PriceApi).apiPriceIdPut(priceToEdit.id!, priceUpdateDto);
                    }
                }
                openNotification("Pomyślnie zaaktualizowano wpis");
            }
            setPrices([])
        } catch (e: any) {
            return e.response.data;
        }

        setAddFormVisible(false)
        await refreshMenuEntryList();
    }

    const onPriceSubmit = (values: PriceCreateDto) => {
        setPrices([...prices!, values])
    }

    const onPriceEdit = (values: PriceUpdateDto[]) => {
        setPricesToEdit(values);
    }

    const onPriceDelete = (record: PriceDto) => {
        let idx = prices?.indexOf(record);
        prices?.splice(idx!,1);
        setPrices([...prices!]);
    }

    const onPositionChangeClicked = async (id: number, direction: Direction) => {
        const menuEntries = await apiService.getApi(MenuEntryApi).apiMenuEntryPositionIdDirectionPatch(id, direction);
        let filteredMenuEntry = menuEntries.data.filter(s => s.menuCategoryId == +menuCategoryId!)
        let sortedMenuEntries = filteredMenuEntry.sort((a, b) => {
            return a.position! - b.position!
        })
        setMenuEntry(sortedMenuEntries)
    }

    return <>
        <Layout style={{backgroundColor: "white"}}>
            <Title level={1} ellipsis={true}>Wpisy menu dla kategorii {menuCategories?.find(c => c.id === +menuCategoryId!)!.name}</Title>
            <Content className={styles.content}>
                <MenuEntryList
                    data={menuEntry || null}
                    onAddClicked={onAddClicked}
                    onEditClicked={onEditClicked}
                    onDeleteClicked={onDeleteClicked}
                    onPositionChangeClicked={onPositionChangeClicked}
                />
                <MenuEntryModalFormAddContainer
                    onFinish={() => setAddFormVisible(false)}
                    onSubmit={onSubmit}
                    onPriceSubmit={onPriceSubmit}
                    onPriceEdit={onPriceEdit}
                    addFormVisibility={isAddFormVisible}
                    title={menuEntryModalFormContainerTitle || ""}
                    MenuEntryToEdit={menuEntryToEdit}
                    menuCategories={menuCategories!}
                    onPriceDelete={onPriceDelete}
                />
            </Content>
        </Layout>
    </>
}
